import { Box } from '@mui/material';
import { ReactNode } from 'react';

export type MagicLinkActionBarProps = {
    /** Primary CTA (e.g. "Pokračovať", "Potvrdiť žiadosť"). Always rendered. */
    primary: ReactNode;
    /** Optional secondary action (e.g. "Späť"). When supplied, the row is split 50/50 on mobile. */
    secondary?: ReactNode;
};

const MOBILE_BAR_HEIGHT_PX = 88;

/**
 * Action row shared by every non-intro magic-link view (request detail, upload id card,
 * extracted data, success, invalid id card ...).
 *
 * Layout:
 * - **Mobile (< sm)**: row is **fixed at the bottom of the viewport** with a top
 *   divider and white background. Buttons split the row 50/50 when both `primary`
 *   and `secondary` are provided, otherwise the primary spans the full width. A
 *   spacer is rendered in flow so the page column reserves room for the bar.
 * - **Desktop (>= sm)**: row is rendered inline inside the page column, right
 *   aligned, with the primary button keeping a 220px minimum width. Secondary
 *   shrinks to its content.
 */
const MagicLinkActionBar = ({ primary, secondary }: MagicLinkActionBarProps) => {
    return (
        <>
            {/* Spacer that reserves height for the fixed bar on mobile so the last
                block in the page column isn't covered by it. Collapses on desktop. */}
            <Box
                aria-hidden
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    flexShrink: 0,
                    height: `${MOBILE_BAR_HEIGHT_PX}px`,
                }}
            />

            <Box
                sx={(theme) => ({
                    position: { xs: 'fixed', sm: 'static' },
                    bottom: { xs: 0, sm: 'auto' },
                    left: { xs: 0, sm: 'auto' },
                    right: { xs: 0, sm: 'auto' },
                    zIndex: { xs: 1100, sm: 'auto' },
                    backgroundColor: { xs: theme.palette.background.default, sm: 'transparent' },
                    paddingX: { xs: 2, sm: 0 },
                    paddingTop: { xs: 2, sm: 0 },
                    paddingBottom: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 0 },
                    marginTop: { sm: 1 },
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: { xs: 'space-between', sm: 'flex-end' },
                    gap: 2,
                    width: '100%',
                })}
            >
                {secondary && (
                    <Box
                        sx={{
                            width: 'fit-content',
                            display: 'flex',
                            minWidth: 0,
                        }}
                    >
                        {secondary}
                    </Box>
                )}
                <Box
                    sx={{
                        flex: { xs: 1, sm: '0 0 auto' },
                        display: 'flex',
                        minWidth: { sm: 220 },
                    }}
                >
                    {primary}
                </Box>
            </Box>
        </>
    );
};

export default MagicLinkActionBar;
